import { useCallback, useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { ChevronLeft, ChevronRight, Download, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";

import { useWarehouseScope } from "../access/useWarehouseScope";
import { apiFetch } from "../api/apiFetch";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";

const PAGE_SIZE = 25;
const DOWNLOAD_PAGE_SIZE = 200;
const WARNING_AGE_MS = 24 * 60 * 60 * 1000;

// Pseudo-warehouse pills that expand into a list of real warehouses when
// the BE call goes out — same convention as Totes / Inventory so "UK"
// means the same thing on every warehouse admin page.
const WAREHOUSE_GROUPS: Record<string, string[]> = {
    "group:UK": ["Newport", "Pembroke"],
};
const expandWarehouseFilter = (raw: string): string[] => {
    if (!raw) return [];
    return WAREHOUSE_GROUPS[raw] ?? [raw];
};

type PutawaySource = "book_in" | "move_stock" | "bf_scan" | "drop";
type ToteType = "box" | "pallet";

interface PutawayQueueRow {
    tote_id: string;
    type: ToteType;
    warehouse: string;
    closed_at: string;
    closed_by: string | null;
    units_total: number;
    units_remaining: number;
    category: string | null;
    source: PutawaySource;
}

interface PutawayWarehouseBreakdown {
    warehouse: string;
    totes: number;
    units_remaining: number;
}

interface PutawayQueueSummary {
    units_remaining: number;
    oldest_closed_at: string | null;
    /** Tote counts per warehouse across every site the caller may see
     *  (other filters applied) — drives the counts on the warehouse pills. */
    by_warehouse?: PutawayWarehouseBreakdown[];
}

interface PutawayQueueResponse {
    rows: PutawayQueueRow[];
    total: number;
    page: number;
    page_size: number;
    summary?: PutawayQueueSummary;
}

const TEXT_COLUMNS = new Set<string>(["tote_id"]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Putaway Queue");
    ws.addRow(header as string[]);
    for (const r of rows) {
        const added = ws.addRow(r);
        for (let c = 1; c <= r.length; c++) {
            added.getCell(c).value = cellToString(added.getCell(c));
        }
    }
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// dd/mm/yyyy hh:mm:ss in local time — matches the inventory pages so
// admins moving between Totes / Inventory see one date format.
const formatDateTime = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

type Translate = (key: string, params?: Record<string, string | number>) => string;

const ageMs = (iso: string | null | undefined) => {
    if (!iso) return null;
    const timestamp = new Date(iso).getTime();
    return Number.isNaN(timestamp) ? null : Math.max(0, Date.now() - timestamp);
};

const formatAge = (iso: string | null | undefined, t: Translate) => {
    const elapsed = ageMs(iso);
    if (elapsed === null) return "—";

    const totalMinutes = Math.floor(elapsed / 60_000);
    if (totalMinutes < 60) {
        return t("putaway_queue_age_minutes", { minutes: totalMinutes });
    }

    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours < 24) {
        const minutes = totalMinutes % 60;
        return minutes === 0
            ? t("putaway_queue_age_hours", { hours: totalHours })
            : t("putaway_queue_age_hours_minutes", { hours: totalHours, minutes });
    }

    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return hours === 0
        ? t("putaway_queue_age_days", { days })
        : t("putaway_queue_age_days_hours", { days, hours });
};

const isOlderThanWarningAge = (iso: string) => {
    const elapsed = ageMs(iso);
    return elapsed !== null && elapsed > WARNING_AGE_MS;
};

const categoryText = (category: string | null, source: PutawaySource, t: Translate, emoji: boolean) => {
    if (!category) return "—";
    const normalized = category.toUpperCase();

    if (normalized === "HOT") return `${emoji ? "🔥 " : ""}${t("bf_colour_hot")}`;
    if (normalized === "COLD") return `${emoji ? "❄️ " : ""}${t("bf_colour_cold")}`;
    if (normalized === "MEASURE") return `${emoji ? "📏 " : ""}${t("bf_colour_measure")}`;
    if (normalized === "RED") {
        if (source === "bf_scan") return `${emoji ? "🔴 " : ""}${t("bf_colour_red")}`;
        return t("colour_red");
    }

    const goodsInKeys: Record<string, string> = {
        BLUE: "colour_blue",
        GREEN: "colour_green",
        GREY: "colour_grey",
        ORANGE: "colour_orange",
        PINK: "colour_pink",
        YELLOW: "colour_yellow",
        MISC: "category_misc",
    };
    const key = goodsInKeys[normalized];
    return key ? t(key) : category;
};

const sourceText = (source: PutawaySource, t: Translate) => {
    const keys: Record<PutawaySource, string> = {
        book_in: "book_in",
        move_stock: "move_stock",
        bf_scan: "scan_sheet",
        drop: "putaway_queue_source_drop",
    };
    return t(keys[source]);
};

const typeText = (type: ToteType, t: Translate) => t(`putaway_queue_type_${type}`);

const makeParams = (
    page: number,
    pageSize: number,
    search: string,
    category: string,
    source: string,
    type: string,
    warehouse: string,
) => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("page_size", String(pageSize));
    const expandedWh = expandWarehouseFilter(warehouse);
    if (expandedWh.length > 0) params.set("warehouse", expandedWh.join(","));
    if (search.trim()) params.set("q", search.trim());
    if (category) params.set("category", category);
    if (source) params.set("source", source);
    if (type) params.set("type", type);
    return params;
};

const PutawayQueueAdmin = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<PutawayQueueRow[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<PutawayQueueSummary | undefined>();
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const [search, setSearch] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(search);
    const [sourceFilter, setSourceFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [page, setPage] = useState(0);

    // Warehouse filter (2026-10-19). "" = every warehouse the caller may see;
    // without cross_warehouse the person is pinned to their own site (no All,
    // no group pills), matching the BE's scopedWarehouseList.
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState("");
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [settingsWarehouses, setSettingsWarehouses] = useState<string[]>([]);

    useEffect(() => { setSearchInput(search); }, [search]);
    useEffect(() => { setPage(0); }, [search, sourceFilter, categoryFilter, typeFilter, warehouseFilter]);

    // Warehouse names for the pill row — loaded once on mount so a site with
    // an empty queue still gets a pill (count 0).
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: unknown = await res.json();
                if (!Array.isArray(data)) return;
                const names = data
                    .map(r => (r as { warehouse?: unknown })?.warehouse)
                    .filter((w): w is string => typeof w === "string" && w.trim() !== "");
                setSettingsWarehouses([...new Set(names)].sort());
            } catch { /* leave empty — pills fall back to the warehouses in the queue */ }
        })();
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = makeParams(
                page,
                PAGE_SIZE,
                search,
                categoryFilter,
                sourceFilter,
                typeFilter,
                warehouseFilter,
            );
            const res = await apiFetch(`/put-away/queue/details?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: PutawayQueueResponse = await res.json();
            setRows(Array.isArray(data.rows) ? data.rows : []);
            setTotal(typeof data.total === "number" ? data.total : 0);
            setSummary(data.summary);
        } catch {
            toast.error(t("server_error"), { toastId: "putaway-queue-load-err" });
        } finally {
            setLoading(false);
        }
    }, [page, search, categoryFilter, sourceFilter, typeFilter, warehouseFilter, t]);

    useEffect(() => { void refresh(); }, [refresh]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
        setPage(current => (current > maxPage ? maxPage : current));
    }, [total]);

    useEffect(() => {
        let intervalId: ReturnType<typeof setInterval> | undefined;

        const stopInterval = () => {
            if (intervalId !== undefined) {
                clearInterval(intervalId);
                intervalId = undefined;
            }
        };
        const startInterval = () => {
            stopInterval();
            if (document.visibilityState === "visible") {
                intervalId = setInterval(() => { void refresh(); }, 15_000);
            }
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void refresh();
                startInterval();
            } else {
                stopInterval();
            }
        };

        startInterval();
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            stopInterval();
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [refresh]);

    const downloadCurrent = async () => {
        setDownloading(true);
        try {
            const all: PutawayQueueRow[] = [];
            let downloadPage = 0;
            while (true) {
                const params = makeParams(
                    downloadPage,
                    DOWNLOAD_PAGE_SIZE,
                    search,
                    categoryFilter,
                    sourceFilter,
                    typeFilter,
                    warehouseFilter,
                );
                const res = await apiFetch(`/put-away/queue/details?${params}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: PutawayQueueResponse = await res.json();
                const pageRows = Array.isArray(data.rows) ? data.rows : [];
                all.push(...pageRows);
                const grandTotal = typeof data.total === "number" ? data.total : all.length;
                if (pageRows.length === 0 || all.length >= grandTotal) break;
                downloadPage += 1;
            }

            await downloadXlsx(
                "putaway-queue-current.xlsx",
                ["tote_id", "warehouse", "type", "closed_at", "closed_by", "age", "units", "category", "source"],
                all.map(row => [
                    row.tote_id,
                    row.warehouse ?? "",
                    typeText(row.type, t),
                    formatDateTime(row.closed_at),
                    row.closed_by ?? "",
                    formatAge(row.closed_at, t),
                    row.units_remaining < row.units_total
                        ? t("putaway_queue_units_of", {
                            remaining: row.units_remaining,
                            total: row.units_total,
                        })
                        : String(row.units_total),
                    row.category ? categoryText(row.category, row.source, t, false) : "",
                    sourceText(row.source, t),
                ]),
            );
        } catch {
            toast.error(t("server_error"), { toastId: "putaway-queue-download-err" });
        } finally {
            setDownloading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    // Pill row: every warehouse from /settings plus any the queue reports
    // (covers a site missing from settings), narrowed to what this person
    // may pick. Counts come from the BE breakdown, which ignores the
    // warehouse pin but keeps the other filters.
    const breakdown = summary?.by_warehouse ?? [];
    const countByWarehouse = new Map(breakdown.map(b => [b.warehouse, b.totes]));
    const warehouses = warehouseScope.limit(
        [...new Set([...settingsWarehouses, ...breakdown.map(b => b.warehouse)])].sort(),
    );
    const groupCount = (members: string[]) =>
        members.reduce((sum, m) => sum + (countByWarehouse.get(m) ?? 0), 0);
    const allCount = warehouseScope.canCross
        ? breakdown.reduce((sum, b) => sum + b.totes, 0)
        : undefined;

    return (
        <div className="totes-admin putaway-queue-admin">
            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { void refresh(); }} disabled={loading}>
                    <RefreshCw size={16} /> {t("refresh")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => { void downloadCurrent(); }}
                    disabled={loading || downloading || total === 0}
                >
                    <Download size={16} /> {t("putaway_queue_download_current")}
                </button>
            </div>

            <div className="locations-summary">
                <div className="locations-summary-total">
                    <span className="locations-summary-total-label">{t("putaway_queue_total_totes")}</span>
                    <strong>{total}</strong>
                </div>
                {summary && (
                    <div className="locations-summary-warehouses">
                        <div className="locations-summary-pill">
                            <span className="locations-summary-pill-label">{t("putaway_queue_units_remaining")}</span>
                            <span className="locations-summary-pill-value">{summary.units_remaining}</span>
                        </div>
                        <div className="locations-summary-pill">
                            <span className="locations-summary-pill-label">{t("putaway_queue_oldest_waiting")}</span>
                            <span className="locations-summary-pill-value">
                                {formatAge(summary.oldest_closed_at, t)}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <FilterPills
                className="warehouse-admin-pills"
                labelKey="filter_warehouse"
                value={warehouseFilter}
                onChange={setWarehouseFilter}
                hideAll={!warehouseScope.canCross}
                allCount={summary ? allCount : undefined}
                options={[
                    ...(!warehouseScope.canCross ? [] : Object.entries(WAREHOUSE_GROUPS))
                        .filter(([, members]) => members.some(m => warehouses.includes(m)))
                        .map(([groupKey, members]) => ({
                            value: groupKey,
                            label: groupKey.replace(/^group:/, ""),
                            count: summary ? groupCount(members) : undefined,
                        })),
                    ...warehouses.map(w => ({
                        value: w,
                        label: w,
                        count: summary ? (countByWarehouse.get(w) ?? 0) : undefined,
                    })),
                ]}
            />
            <FilterPills
                className="warehouse-admin-pills"
                labelKey="putaway_queue_filter_source"
                value={sourceFilter}
                onChange={setSourceFilter}
                options={[
                    { value: "book_in", label: t("book_in") },
                    { value: "move_stock", label: t("move_stock") },
                    { value: "bf_scan", label: t("scan_sheet") },
                    { value: "drop", label: t("putaway_queue_source_drop") },
                ]}
            />
            <FilterPills
                className="warehouse-admin-pills"
                labelKey="putaway_queue_filter_category"
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={[
                    { value: "HOT", label: "🔥" },
                    { value: "COLD", label: "❄️" },
                    { value: "RED", label: "🔴" },
                    { value: "MEASURE", label: "📏" },
                    { value: "none", label: t("putaway_queue_category_none") },
                ]}
            />
            <FilterPills
                className="warehouse-admin-pills"
                labelKey="putaway_queue_filter_type"
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                    { value: "box", label: t("putaway_queue_type_box") },
                    { value: "pallet", label: t("putaway_queue_type_pallet") },
                ]}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("putaway_queue_search_hint")}
                    value={searchInput}
                    onChange={event => setSearchInput(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            setSearch(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== search.trim()) setSearch(searchInput.trim());
                    }}
                />
            </div>

            {loading && rows.length === 0 ? (
                <div className="locations-table-empty">{t("loading")}</div>
            ) : total === 0 ? (
                <div className="locations-table-empty">{t("putaway_queue_no_rows")}</div>
            ) : (
                <>
                    <div className="locations-table-wrap">
                        <table className="locations-table putaway-queue-table">
                            <thead>
                                <tr>
                                    <th>{t("putaway_queue_col_tote")}</th>
                                    <th>{t("putaway_queue_col_warehouse")}</th>
                                    <th>{t("putaway_queue_col_type")}</th>
                                    <th>{t("putaway_queue_col_closed")}</th>
                                    <th>{t("putaway_queue_col_closed_by")}</th>
                                    <th>{t("putaway_queue_col_age")}</th>
                                    <th className="locations-table-num">{t("putaway_queue_col_units")}</th>
                                    <th>{t("putaway_queue_col_category")}</th>
                                    <th>{t("putaway_queue_col_source")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.tote_id}>
                                        <td className="locations-table-mono">{row.tote_id}</td>
                                        <td>{row.warehouse || "—"}</td>
                                        <td>{typeText(row.type, t)}</td>
                                        <td>{formatDateTime(row.closed_at)}</td>
                                        <td>{row.closed_by ?? "—"}</td>
                                        <td className={isOlderThanWarningAge(row.closed_at) ? "putaway-queue-age--warning" : undefined}>
                                            {formatAge(row.closed_at, t)}
                                        </td>
                                        <td className="locations-table-num">
                                            {row.units_remaining < row.units_total
                                                ? t("putaway_queue_units_of", {
                                                    remaining: row.units_remaining,
                                                    total: row.units_total,
                                                })
                                                : row.units_total}
                                        </td>
                                        <td>{categoryText(row.category, row.source, t, true)}</td>
                                        <td>{sourceText(row.source, t)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="locations-table-footer">
                        <span>{t("locations_showing_range", {
                            from: String(rangeFrom),
                            to: String(rangeTo),
                            total: String(total),
                        })}</span>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(current => Math.max(0, current - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(current => Math.min(totalPages - 1, current + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PutawayQueueAdmin;
